import React, {
	FC, useEffect, useMemo, useState,
} from 'react';
import {
	Checkbox,
	Counter,
	Label,
	Menu,
	ArrayElement,
	IColumnEditor,
} from '@aleph/shared-components';
import { getRedditDeviceOptions } from 'wb-reddit/app/routes/superGrid/targeting/utils';
import { useApolloClient } from '@apollo/client';
import { DeviceOptionsQuery } from 'wb-reddit/graphql/generated';
import { Loader } from 'rsuite';

const DevicesColumnEditor: FC<IColumnEditor<DeviceOptionsQuery['options']['items']>> = (params) => {
	const { value, setValue, editorContext } = params;
	const client = useApolloClient();

	const initialValues = useMemo(() => value, []);

	const [loading, setLoading] = useState<boolean>(true);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const [options, setOptions] = useState<DeviceOptionsQuery['options']['items']>([]);

	const getDevices = async () => {
		setLoading(true);

		const { options: redditOptions, hasMore: hasMoreOptions } = await getRedditDeviceOptions({
			client,
			search:     editorContext?.search,
			deviceType: editorContext?.deviceType,
		});

		setLoading(false);
		setOptions(redditOptions);
		setHasMore(hasMoreOptions);
	};

	const toggleValue = (option: ArrayElement<DeviceOptionsQuery['options']['items']>) => () => {
		if (value?.some(v => v?.value === option?.value)) {
			setValue(value?.filter(v => v?.value !== option?.value));

			return;
		}

		setValue([
			...(value ?? []),
			option,
		]);
	};

	const toggleAll = () => {
		setValue((value?.length ?? 0) > 0 ? [] : options);
	};

	useEffect(() => {
		if (editorContext) {
			getDevices();
		}
	}, [editorContext?.search ?? '', JSON.stringify(editorContext?.deviceType)]);

	return (
		<Menu
			className="flex flex-col min-h-full p-[8px]"
		>
			<>
				{loading
					&& <Loader
						vertical
						size="md"
						className="flex-1 !flex items-center justify-center"
						content="Loading devices"
					/>
				}

				{!loading
					&& <Menu.Item selected={value?.length === options?.length}
						onClick={toggleAll}
						alignItems="space-between"
					>
						<Label className="text-gray-600 text-[12px]">
							SELECT ALL
						</Label>
						{(value?.length ?? 0) > 0
							&& <Counter
								variant="square-accent"
								className="relative"
								count={value?.length ?? 0}
							/>
						}
						<Checkbox
							onClick={toggleAll}
							partial={(value?.length ?? 0) > 0 && value?.length !== options?.length}
							checked={value?.length === options?.length}
						/>
					</Menu.Item>
				}

				{!loading
					&& initialValues?.map(option => (
						<Menu.Item
							onClick={toggleValue(option)}
							selected={value?.some(v => v?.value === option?.value)}
							alignItems="end"
							key={option?.value}
						>
							<Label>
								{option?.label}
							</Label>
							<Label className="text-gray-700 text-[12px]">
								{option?.make}
							</Label>
							<Checkbox checked={value?.some(v => v?.value === option?.value)} />
						</Menu.Item>
					))
				}

				{!loading
					&& options?.filter(
						option => initialValues?.every(v => v?.value !== option?.value) ?? true,
					)?.map(option => (
						<Menu.Item
							onClick={toggleValue(option)}
							selected={value?.some(v => v?.value === option?.value)}
							alignItems="end"
							key={option?.value}
						>
							<Label>
								{option?.label}
							</Label>
							<Label className="text-gray-700 text-[12px]">
								{option?.make}
							</Label>
							<Checkbox checked={value?.some(v => v?.value === option?.value)} />
						</Menu.Item>
					))
				}

				{!loading
					&& hasMore
					&& <div className="flex items-center justify-center text-gray-700 py-[10px]">
						Search to find more devices
					</div>
				}
			</>
		</Menu>
	);
};

export default DevicesColumnEditor;
